import { useState, useMemo, useEffect, useCallback } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  createColumnHelper,
  type PaginationState,
  type SortingState,
} from '@tanstack/react-table';
import { Plus, Pencil, Trash2, Search, RefreshCw, ArrowLeft, Eraser, ArrowUpNarrowWide, ArrowDownWideNarrow, ArrowUpDown, HelpCircle, X, Users, Shield, Eye, Mail, Clock, CheckCircle2, XCircle, UserPlus, Bell, ShieldCheck, Phone } from 'lucide-react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  useAdminUserList,
  useCreateAdminUser,
  useUpdateAdminUser,
  useDeleteAdminUser,
  useToggleNotification,
} from '../hooks/useAdminUsers.ts';
import { ConfirmModal } from '@/shared/components/ConfirmModal.tsx';
import { RowActionMenu, type RowAction } from '@/shared/components/RowActionMenu.tsx';
import { InfoTooltip, TooltipHeader, TooltipBody, TooltipDetail, TooltipTip } from '@/shared/components/InfoTooltip.tsx';
import type { AdminUserRow, AdminUserListParams, AdminPerfil } from '@/shared/types/admin-user.ts';

type StatusFilter = 'all' | 'active' | 'inactive' | 'pending';
type ViewMode = 'list' | 'form';

const columnHelper = createColumnHelper<AdminUserRow>();

const createSchema = z.object({
  nome: z.string().min(2, 'Nome deve ter no mínimo 2 caracteres'),
  email: z
    .string()
    .email('Email inválido')
    .refine((e) => e.endsWith('@woli.com.br'), 'Email deve ser @woli.com.br'),
  perfil: z.enum(['ADMIN', 'VIEWER']),
  recebeNotificacoes: z.boolean(),
  phone: z.string().optional(),
});

const updateSchema = z.object({
  nome: z.string().min(2, 'Nome deve ter no mínimo 2 caracteres'),
  email: z.string(),
  perfil: z.enum(['ADMIN', 'VIEWER']),
  isActive: z.boolean(),
  recebeNotificacoes: z.boolean(),
  phone: z.string().optional(),
});

type CreateFormData = z.infer<typeof createSchema>;
type UpdateFormData = z.infer<typeof updateSchema>;

// ── Badges ──────────────────────────────────────────────────

function StatusBadge({ user }: { user: AdminUserRow }) {
  if (!user.isActive && !user.ultimoAcesso) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900/30 dark:text-amber-400">
        <Clock size={12} /> Pendente
      </span>
    );
  }
  if (!user.isActive) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800 dark:bg-red-900/30 dark:text-red-300">
        <XCircle size={12} /> Inativo
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium text-white" style={{ background: 'var(--color-status-up)' }}>
      <CheckCircle2 size={12} /> Ativo
    </span>
  );
}

function PerfilBadge({ perfil }: { perfil: AdminPerfil }) {
  if (perfil === 'ADMIN') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
        <Shield size={12} /> Admin
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-gray-200 px-2 py-0.5 text-xs font-medium text-gray-700 dark:bg-gray-700 dark:text-gray-300">
      <Eye size={12} /> Viewer
    </span>
  );
}

function formatDate(date: string | null) {
  if (!date) return '—';
  return new Date(date).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// ── Modal: Como funciona ────────────────────────────────────

function UserHelpModal({ onClose }: { onClose: () => void }) {
  const handleBackdrop = useCallback((e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={handleBackdrop}
    >
      <div className="relative mx-4 w-full max-w-lg rounded-xl border border-gray-200 bg-white shadow-2xl dark:border-gray-700 dark:bg-gray-900">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-gray-700">
          <div className="flex items-center gap-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-100 text-blue-600 dark:bg-blue-900/40 dark:text-blue-400">
              <HelpCircle size={18} />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Gerenciamento de Usuários</h3>
          </div>
          <button
            onClick={onClose}
            className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800 dark:hover:text-gray-300"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="space-y-5 px-6 py-5">
          {/* Fluxo de acesso */}
          <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-800/50 dark:bg-blue-900/20">
            <div className="mb-2.5 flex items-center gap-2">
              <Users size={16} className="text-blue-600 dark:text-blue-400" />
              <h4 className="text-sm font-semibold text-blue-800 dark:text-blue-300">Fluxo de acesso</h4>
            </div>
            <div className="space-y-1.5 text-xs text-blue-700 dark:text-blue-300/90">
              <p>Quando um colaborador <strong>@woli.com.br</strong> faz login pela primeira vez, é criado automaticamente como <strong>Pendente</strong> (inativo).</p>
              <p>Um administrador deve ativá-lo para liberar o acesso ao painel.</p>
            </div>
          </div>

          {/* Pré-cadastro */}
          <div className="rounded-lg border border-green-200 bg-green-50 p-4 dark:border-green-800/50 dark:bg-green-900/20">
            <div className="mb-2.5 flex items-center gap-2">
              <UserPlus size={16} className="text-green-600 dark:text-green-400" />
              <h4 className="text-sm font-semibold text-green-800 dark:text-green-300">Pré-cadastro</h4>
            </div>
            <div className="space-y-1.5 text-xs text-green-700 dark:text-green-300/90">
              <p>Você pode pré-cadastrar um usuário <strong>antes do primeiro login</strong>. Defina email, perfil e se receberá notificações.</p>
              <p>Quando a pessoa fizer login pelo Google, será associada automaticamente ao cadastro existente.</p>
            </div>
          </div>

          {/* Status */}
          <div className="rounded-lg border border-orange-200 bg-orange-50 p-4 dark:border-orange-800/50 dark:bg-orange-900/20">
            <div className="mb-2.5 flex items-center gap-2">
              <ShieldCheck size={16} className="text-orange-600 dark:text-orange-400" />
              <h4 className="text-sm font-semibold text-orange-800 dark:text-orange-300">Status do Usuário</h4>
            </div>
            <div className="space-y-1.5 text-xs text-orange-700 dark:text-orange-300/90">
              <p><strong>Ativo:</strong> acesso liberado ao painel administrativo</p>
              <p><strong>Inativo:</strong> acesso bloqueado — o login é impedido no callback OAuth</p>
              <p><strong>Pendente:</strong> primeiro login realizado, aguardando aprovação de um admin</p>
            </div>
          </div>

          {/* Notificações */}
          <div className="rounded-lg border border-purple-200 bg-purple-50 p-4 dark:border-purple-800/50 dark:bg-purple-900/20">
            <div className="mb-2.5 flex items-center gap-2">
              <Bell size={16} className="text-purple-600 dark:text-purple-400" />
              <h4 className="text-sm font-semibold text-purple-800 dark:text-purple-300">Notificações por Email</h4>
            </div>
            <div className="space-y-1.5 text-xs text-purple-700 dark:text-purple-300/90">
              <p>Usuários com a flag de notificações ativa recebem email sempre que um incidente é aberto (manual ou automático via anti-flap).</p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 px-6 py-3 dark:border-gray-700">
          <button
            onClick={onClose}
            className="w-full rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
          >
            Entendi
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main Component ───────────────────────────────────────────

export function AdminUserListPage() {
  // ── Estado local ────────────────────────────────────────────
  const [search, setSearch] = useState('');
  const [showHelp, setShowHelp] = useState(false);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: 20 });
  const [sorting, setSorting] = useState<SortingState>([{ id: 'nome', desc: false }]);
  const [searched, setSearched] = useState(false);

  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [editingUser, setEditingUser] = useState<AdminUserRow | null>(null);

  const [confirmDestroy, setConfirmDestroy] = useState<AdminUserRow | null>(null);

  const isEditing = editingUser !== null;

  // ── Form ────────────────────────────────────────────────────
  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm<CreateFormData | UpdateFormData>({
    resolver: zodResolver(isEditing ? updateSchema : createSchema),
    defaultValues: { nome: '', email: '', perfil: 'VIEWER' as AdminPerfil, recebeNotificacoes: false, phone: '' },
  });

  useEffect(() => {
    if (editingUser) {
      reset({
        nome: editingUser.nome,
        email: editingUser.email,
        perfil: editingUser.perfil,
        isActive: editingUser.isActive,
        recebeNotificacoes: editingUser.recebeNotificacoes,
        phone: editingUser.phone ?? '',
      });
    } else {
      reset({ nome: '', email: '', perfil: 'VIEWER', recebeNotificacoes: false, phone: '' });
    }
  }, [editingUser, reset]);

  // ── Queries / mutations ─────────────────────────────────────
  const params = useMemo(
    () => ({
      page: pagination.pageIndex + 1,
      limit: pagination.pageSize,
      search: search || undefined,
      status: statusFilter === 'all' ? undefined : statusFilter,
      sortBy: (sorting[0]?.id as AdminUserListParams['sortBy']) || 'nome',
      sortOrder: (sorting[0]?.desc ? 'desc' : 'asc') as AdminUserListParams['sortOrder'],
    }),
    [pagination.pageIndex, pagination.pageSize, search, statusFilter, sorting],
  );

  const { data: response, isLoading } = useAdminUserList(params, searched);

  const createMutation = useCreateAdminUser(() => { setViewMode('list'); });
  const updateMutation = useUpdateAdminUser(() => { setViewMode('list'); setEditingUser(null); });
  const destroyMutation = useDeleteAdminUser(() => setConfirmDestroy(null));
  const toggleNotifMutation = useToggleNotification();

  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  function onSubmit(data: CreateFormData | UpdateFormData) {
    if (isEditing && editingUser) {
      const d = data as UpdateFormData;
      updateMutation.mutate({
        id: editingUser.id,
        input: { nome: d.nome, perfil: d.perfil, isActive: d.isActive, recebeNotificacoes: d.recebeNotificacoes, phone: d.phone || undefined },
      });
    } else {
      const d = data as CreateFormData;
      createMutation.mutate({ nome: d.nome, email: d.email, perfil: d.perfil, recebeNotificacoes: d.recebeNotificacoes, phone: d.phone || undefined });
    }
  }

  function handleSearch() {
    setSearched(true);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }

  function handleClear() {
    setSearch('');
    setStatusFilter('all');
    setSorting([{ id: 'nome', desc: false }]);
    setSearched(false);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }

  // ── Colunas ─────────────────────────────────────────────────
  const columns = useMemo(
    () => [
      columnHelper.display({
        id: 'actions',
        header: 'Ações',
        enableSorting: false,
        cell: ({ row }) => {
          const u = row.original;
          const actions: RowAction[] = [
            {
              label: 'Editar',
              icon: <Pencil size={15} />,
              onClick: () => { setEditingUser(u); setViewMode('form'); },
            },
            {
              label: 'Excluir',
              icon: <Trash2 size={15} />,
              onClick: () => setConfirmDestroy(u),
              variant: 'danger',
            },
          ];
          return <RowActionMenu actions={actions} />;
        },
      }),
      columnHelper.accessor('nome', {
        header: 'Nome',
        cell: (info) => <span className="font-medium">{info.getValue()}</span>,
      }),
      columnHelper.accessor('email', {
        header: 'Email',
        cell: (info) => <span className="text-gray-500 dark:text-gray-400">{info.getValue()}</span>,
      }),
      columnHelper.accessor('phone', {
        header: 'Telefone',
        enableSorting: false,
        cell: (info) => {
          const v = info.getValue();
          if (!v) return <span className="text-gray-300 dark:text-gray-600">—</span>;
          const formatted = v.replace(/^55(\d{2})(\d{5})(\d{4})$/, '($1) $2-$3');
          return <span className="text-gray-500 dark:text-gray-400">{formatted}</span>;
        },
      }),
      columnHelper.accessor('perfil', {
        header: 'Perfil',
        cell: ({ row }) => <PerfilBadge perfil={row.original.perfil} />,
      }),
      columnHelper.accessor('recebeNotificacoes', {
        header: 'Notif.',
        enableSorting: false,
        cell: ({ row }) => {
          const u = row.original;
          const isPending = toggleNotifMutation.isPending && toggleNotifMutation.variables?.id === u.id;
          return (
            <button
              type="button"
              disabled={isPending}
              onClick={() => toggleNotifMutation.mutate({ id: u.id, current: u.recebeNotificacoes })}
              className={`rounded p-1 transition-colors ${
                u.recebeNotificacoes
                  ? 'text-blue-500 hover:bg-blue-50 hover:text-blue-700 dark:hover:bg-blue-900/30'
                  : 'text-gray-300 hover:bg-gray-100 hover:text-gray-500 dark:text-gray-600 dark:hover:bg-gray-800 dark:hover:text-gray-400'
              } ${isPending ? 'animate-pulse' : ''}`}
              title={u.recebeNotificacoes ? 'Desativar notificações (e-mail + WhatsApp)' : 'Ativar notificações (e-mail + WhatsApp)'}
            >
              <Bell size={16} />
            </button>
          );
        },
      }),
      columnHelper.accessor('isActive', {
        header: 'Status',
        cell: ({ row }) => <StatusBadge user={row.original} />,
      }),
      columnHelper.accessor('ultimoAcesso', {
        header: 'Último Acesso',
        cell: (info) => (
          <span className="text-gray-500 dark:text-gray-400">{formatDate(info.getValue())}</span>
        ),
      }),
    ],
    [toggleNotifMutation],
  );

  // ── Tabela ──────────────────────────────────────────────────
  const table = useReactTable({
    data: response?.data ?? [],
    columns,
    pageCount: response ? Math.ceil(response.total / response.limit) : -1,
    state: { pagination, sorting },
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualSorting: true,
  });

  // ── VIEW: Formulário ────────────────────────────────────────
  if (viewMode === 'form') {
    return (
      <div className="space-y-4">
        <button
          onClick={() => { setViewMode('list'); setEditingUser(null); }}
          className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
        >
          <ArrowLeft size={16} />
          Voltar
        </button>

        <div className="mx-auto max-w-2xl rounded-lg border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-900">
          <div className="border-b-2 border-gray-200 px-6 py-4 dark:border-gray-700">
            <h2 className="text-xl font-semibold">{isEditing ? 'Editar Usuário' : 'Novo Usuário (Pré-cadastro)'}</h2>
          </div>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5 px-6 py-6">
            <div>
              <div className="mb-1.5 flex items-center gap-1.5">
                <label className="text-sm font-medium">Nome *</label>
                <InfoTooltip>
                  <TooltipHeader
                    icon={<Users size={16} />}
                    title="Nome do Usuário"
                    subtitle="Nome de exibição no painel"
                  />
                  <TooltipBody>
                    <TooltipDetail>Identifica o usuário em logs de auditoria e nas telas do sistema.</TooltipDetail>
                    <TooltipDetail><strong>Limite:</strong> Mínimo de 2 caracteres</TooltipDetail>
                  </TooltipBody>
                  <TooltipTip><strong>Dica:</strong> Use o nome completo do colaborador</TooltipTip>
                </InfoTooltip>
              </div>
              <input
                {...register('nome')}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800"
                placeholder="Nome completo"
              />
              {errors.nome && <p className="mt-1 text-xs text-red-500">{errors.nome.message}</p>}
            </div>

            <div>
              <div className="mb-1.5 flex items-center gap-1.5">
                <label className="text-sm font-medium">Email *</label>
                <InfoTooltip>
                  <TooltipHeader
                    icon={<Mail size={16} />}
                    title="Email Corporativo"
                    subtitle="Deve ser @woli.com.br"
                  />
                  <TooltipBody>
                    <TooltipDetail>O email é usado para identificar o usuário no login via Google OAuth.</TooltipDetail>
                    <TooltipDetail><strong>Importante:</strong> Não pode ser alterado após a criação.</TooltipDetail>
                  </TooltipBody>
                  <TooltipTip><strong>Exemplo:</strong> "joao.silva@woli.com.br"</TooltipTip>
                </InfoTooltip>
              </div>
              <input
                {...register('email')}
                readOnly={isEditing}
                className={`w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 ${
                  isEditing ? 'cursor-not-allowed bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400' : ''
                }`}
                placeholder="colaborador@woli.com.br"
              />
              {errors.email && <p className="mt-1 text-xs text-red-500">{errors.email.message}</p>}
              {!isEditing && (
                <p className="mt-1 text-xs text-gray-400">Não pode ser alterado depois da criação.</p>
              )}
            </div>

            <div>
              <div className="mb-1.5 flex items-center gap-1.5">
                <label className="text-sm font-medium">Telefone (WhatsApp)</label>
                <InfoTooltip>
                  <TooltipHeader
                    icon={<Phone size={16} />}
                    title="Telefone WhatsApp"
                    subtitle="Para notificações de incidentes"
                  />
                  <TooltipBody>
                    <TooltipDetail>Usado para enviar alertas de incidentes via WhatsApp (canal paralelo ao e-mail).</TooltipDetail>
                    <TooltipDetail><strong>Formato:</strong> DDD + numero (ex: 11999998888)</TooltipDetail>
                  </TooltipBody>
                  <TooltipTip><strong>Dica:</strong> Apenas celulares brasileiros. O numero sera normalizado automaticamente.</TooltipTip>
                </InfoTooltip>
              </div>
              <input
                {...register('phone')}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800"
                placeholder="11999998888"
              />
              {errors.phone && <p className="mt-1 text-xs text-red-500">{errors.phone.message}</p>}
            </div>

            <div>
              <div className="mb-1.5 flex items-center gap-1.5">
                <label className="text-sm font-medium">Perfil *</label>
                <InfoTooltip>
                  <TooltipHeader
                    icon={<Shield size={16} />}
                    title="Perfil de Acesso"
                    subtitle="Define as permissões do usuário"
                  />
                  <TooltipBody>
                    <TooltipDetail><strong>Admin:</strong> acesso completo ao painel, pode gerenciar usuários</TooltipDetail>
                    <TooltipDetail><strong>Viewer:</strong> acesso somente leitura ao painel</TooltipDetail>
                  </TooltipBody>
                </InfoTooltip>
              </div>
              <select
                {...register('perfil')}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800"
              >
                <option value="VIEWER">Viewer</option>
                <option value="ADMIN">Admin</option>
              </select>
              {errors.perfil && <p className="mt-1 text-xs text-red-500">{errors.perfil.message}</p>}
            </div>

            {isEditing && (
              <div>
                <label className="mb-1.5 block text-sm font-medium">Status</label>
                <Controller
                  name="isActive"
                  control={control}
                  render={({ field }) => (
                    <select
                      value={field.value ? 'true' : 'false'}
                      onChange={(e) => field.onChange(e.target.value === 'true')}
                      className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800"
                    >
                      <option value="true">Ativo</option>
                      <option value="false">Inativo</option>
                    </select>
                  )}
                />
              </div>
            )}

            <Controller
              name="recebeNotificacoes"
              control={control}
              render={({ field }) => (
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300 accent-blue-600"
                  />
                  Receber notificações de incidentes por email
                </label>
              )}
            />

            <div className="flex justify-end gap-3 border-t border-gray-200 pt-5 dark:border-gray-700">
              <button
                type="button"
                onClick={() => { setViewMode('list'); setEditingUser(null); }}
                className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {isSubmitting ? 'Salvando...' : isEditing ? 'Salvar' : 'Criar'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  // ── VIEW: Lista ─────────────────────────────────────────────
  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Usuários</h1>
          <p className="mt-0.5 flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400">
            Gerencie os usuários do painel administrativo
            <button
              onClick={() => setShowHelp(true)}
              className="rounded-full p-0.5 text-gray-400 transition-colors hover:bg-blue-50 hover:text-blue-600 dark:hover:bg-blue-900/30 dark:hover:text-blue-400"
              title="Como funciona o gerenciamento de usuários?"
            >
              <HelpCircle size={16} />
            </button>
          </p>
        </div>
        <button
          onClick={() => { setEditingUser(null); setViewMode('form'); }}
          className="flex items-center gap-1.5 rounded-md bg-blue-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-blue-700"
        >
          <Plus size={16} />
          Novo Usuário
        </button>
      </div>

      {/* Painel de filtros */}
      <div className="rounded border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-900">
        <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div>
            <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Buscar</label>
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPagination((prev) => ({ ...prev, pageIndex: 0 }));
                }}
                placeholder="Buscar por nome ou email..."
                className="w-full rounded-md border border-gray-300 py-2 pl-9 pr-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800"
              />
            </div>
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Status</label>
            <div className="flex items-center gap-4 pt-1">
              {([['all', 'Todos'], ['active', 'Ativos'], ['inactive', 'Inativos'], ['pending', 'Pendentes']] as const).map(([value, label]) => (
                <label key={value} className="flex cursor-pointer items-center gap-1.5 text-sm text-gray-700 dark:text-gray-300">
                  <input
                    type="radio"
                    name="statusFilter"
                    value={value}
                    checked={statusFilter === value}
                    onChange={() => {
                      setStatusFilter(value as StatusFilter);
                      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
                    }}
                    className="h-4 w-4 cursor-pointer accent-blue-600"
                  />
                  {label}
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* Ações: Pesquisar + Limpar */}
        <div className="flex items-center gap-2">
          <button
            onClick={handleSearch}
            className="flex items-center gap-1.5 rounded-md bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700"
            title="Utilize os filtros para buscar e filtrar os usuários"
          >
            <Search size={16} />
            Pesquisar
          </button>
          <button
            onClick={handleClear}
            className="flex items-center gap-1.5 rounded-md bg-gray-400 px-4 py-2 text-sm font-medium text-white hover:bg-gray-500"
          >
            <Eraser size={16} />
            Limpar
          </button>
        </div>
      </div>

      {/* Tabela */}
      <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900">
        {!searched ? (
          <div className="flex h-48 flex-col items-center justify-center gap-1 text-gray-400">
            <Search size={24} className="mb-1" />
            <p className="text-sm font-medium">Utilize os filtros acima e clique em Pesquisar</p>
          </div>
        ) : isLoading ? (
          <div className="flex h-48 items-center justify-center">
            <RefreshCw size={24} className="animate-spin text-gray-400" />
          </div>
        ) : response && response.data.length === 0 ? (
          <div className="flex h-48 flex-col items-center justify-center gap-1 text-gray-400">
            <p className="text-sm font-medium">Nenhum usuário encontrado.</p>
            <p className="text-xs">Adicione novos usuários ou ajuste os filtros de busca.</p>
          </div>
        ) : (
          <table className="w-full text-left text-sm">
            <thead>
              {table.getHeaderGroups().map((hg) => (
                <tr key={hg.id} className="border-b-2 border-gray-200 bg-gray-50 text-xs uppercase text-gray-500 dark:border-gray-700 dark:bg-gray-800/50 dark:text-gray-400">
                  {hg.headers.map((header) => {
                    const canSort = header.column.getCanSort();
                    return (
                      <th
                        key={header.id}
                        className={`px-4 py-3 ${canSort ? 'cursor-pointer select-none' : ''}`}
                        onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                      >
                        <span className="inline-flex items-center gap-1">
                          {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                          {canSort && (
                            header.column.getIsSorted() === 'asc'
                              ? <ArrowUpNarrowWide size={14} className="text-blue-600 dark:text-blue-400" />
                              : header.column.getIsSorted() === 'desc'
                                ? <ArrowDownWideNarrow size={14} className="text-blue-600 dark:text-blue-400" />
                                : <ArrowUpDown size={14} className="text-gray-300 dark:text-gray-600" />
                          )}
                        </span>
                      </th>
                    );
                  })}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row) => (
                <tr key={row.id} className={`border-b border-gray-100 transition-colors hover:bg-blue-50/30 dark:border-gray-800 dark:hover:bg-blue-900/10 ${Number(row.id) % 2 === 1 ? 'bg-gray-50/50 dark:bg-gray-800/30' : ''}`}>
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} className="px-4 py-3">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Paginação */}
        {searched && response && response.total > 0 && (
          <div className="flex items-center justify-between border-t border-gray-200 px-4 py-3 text-sm dark:border-gray-700">
            <span className="text-gray-500 dark:text-gray-400">
              Mostrando {Math.min(pagination.pageIndex * pagination.pageSize + 1, response.total)} a{' '}
              {Math.min((pagination.pageIndex + 1) * pagination.pageSize, response.total)} de {response.total} registros
            </span>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1.5 text-gray-500 dark:text-gray-400">
                <span className="text-xs">Resultados por página:</span>
                <select
                  value={pagination.pageSize}
                  onChange={(e) => setPagination({ pageIndex: 0, pageSize: Number(e.target.value) })}
                  className="rounded border border-gray-300 px-1.5 py-0.5 text-xs dark:border-gray-600 dark:bg-gray-800"
                >
                  {[10, 20, 50, 100].map((size) => (
                    <option key={size} value={size}>{size}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => table.previousPage()}
                  disabled={!table.getCanPreviousPage()}
                  className="rounded border border-gray-300 px-3 py-1 text-sm disabled:opacity-40 dark:border-gray-600"
                >
                  Anterior
                </button>
                <span className="px-2 text-xs text-gray-500 dark:text-gray-400">
                  {pagination.pageIndex + 1} / {table.getPageCount()}
                </span>
                <button
                  onClick={() => table.nextPage()}
                  disabled={!table.getCanNextPage()}
                  className="rounded border border-gray-300 px-3 py-1 text-sm disabled:opacity-40 dark:border-gray-600"
                >
                  Próxima
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modal de confirmação de exclusão */}
      {confirmDestroy && (
        <ConfirmModal
          title="Excluir Usuário"
          message={`Deseja excluir o usuário "${confirmDestroy.nome}" (${confirmDestroy.email})? O usuário não poderá mais acessar o painel.`}
          confirmLabel="Excluir"
          onConfirm={() => destroyMutation.mutate(confirmDestroy.id)}
          onCancel={() => setConfirmDestroy(null)}
          isLoading={destroyMutation.isPending}
        />
      )}

      {/* Modal de ajuda */}
      {showHelp && <UserHelpModal onClose={() => setShowHelp(false)} />}
    </div>
  );
}
